/**
 * IS THIS CALL STILL A CALL? — asked twice, answered by two different rules, on
 * purpose.
 *
 * A client learns a call is over in two ways, and they are not the same kind of
 * fact:
 *
 *   • AN EVENT. The server has DECIDED something and is announcing it. The
 *     decision is already made; this client is only being told.
 *   • A POLL. Nobody announced anything. `useCallReconciliation` is asking
 *     because a frame may have been missed, and it is INFERRING.
 *
 * Both used to be answered by one set of live statuses and the negation of it,
 * and each direction of that negation is a bug:
 *
 *   ENDING ON AN UNKNOWN STATUS. Reconciliation treated everything not in its
 *   hardcoded live set as terminal, so a status this client has never heard of
 *   — a `parked` from a newer API, and `pstn_park_call` already exists in-tree
 *   — ends the call and stops the microphone within 30 seconds. Inference must
 *   never act on a word it does not know.
 *
 *   REFUSING TO END ON AN UNKNOWN STATUS. The mirror image, and far worse: it
 *   is the 68-minute call. If an announcement is obeyed only when the payload
 *   is understood, then a payload that is missing, or from a newer server, is a
 *   microphone that never stops. An announcement is obeyed unless the server
 *   explicitly contradicts it.
 *
 * So `callIsLive` and `callHasFinished` are NOT each other's negation, and an
 * unknown or absent status is `false` from both. That is the whole file.
 */
import type { CallStatus } from '@cyphex/types/calls';

/**
 * Every status, classified. A `Record` over the union rather than two sets,
 * because adding a `CallStatus` member without saying which side it falls on is
 * then a TYPE ERROR here — which is exactly the landmine this replaces.
 */
const PHASE: Readonly<Record<CallStatus, 'live' | 'finished'>> = {
  ringing: 'live',
  connecting: 'live',
  active: 'live',
  on_hold: 'live',
  /* A transfer in flight is a call in progress. Deliberately live: on every
     question it is asked, this file errs toward leaving a call alone. */
  transferring: 'live',
  ended: 'finished',
  missed: 'finished',
  declined: 'finished',
  failed: 'finished',
  busy: 'finished',
};

function phaseOf(status: unknown): 'live' | 'finished' | undefined {
  if (typeof status !== 'string') return undefined;
  /* `Object.hasOwn`, not `PHASE[status] !== undefined`: an index on a Record is
     a lie about a string that is not a key, and `constructor` would answer. */
  return Object.hasOwn(PHASE, status) ? PHASE[status as CallStatus] : undefined;
}

/**
 * The server SAYS this call is still running.
 *
 * Only a known live status is a yes. Use it to REFUSE to act on an announced
 * ending: one person declining a call that is still ringing for somebody else
 * is the case it exists for.
 */
export function callIsLive(status: unknown): boolean {
  return phaseOf(status) === 'live';
}

/**
 * The server SAYS this call is over.
 *
 * Only a known terminal status is a yes. Use it to END a call on inference — a
 * poll, never an announcement.
 */
export function callHasFinished(status: unknown): boolean {
  return phaseOf(status) === 'finished';
}

/**
 * `event.call.status` off a realtime frame, without assuming either half is
 * there.
 *
 * `CallStateEvent.call` is typed `Call`, so TypeScript says neither can be
 * missing. Fixtures, mocks and older API versions say otherwise, and
 * `useCallReconciliation.endingFor` already carries a comment about four tests
 * that caught exactly that shape. An absent payload has to read as "the server
 * said nothing" — not as an exception, and not as a live call.
 *
 * Takes the whole event rather than `event.call` so that callers do not have to
 * narrow a union before they can ask; `unknown` in, `unknown` out, and the two
 * predicates above decide what the answer means.
 */
export function announcedCallStatus(event: unknown): unknown {
  if (typeof event !== 'object' || event === null) return undefined;
  if (!('call' in event)) return undefined;
  const call: unknown = event.call;
  if (typeof call !== 'object' || call === null) return undefined;
  return 'status' in call ? call.status : undefined;
}
